import type { ReactNode } from 'react'
import { useCallback } from 'react'
import { chain } from 'react-aria'
import type { DialogProps } from 'react-aria-components'
import Button from './Button'
import Dialog, { DialogBody, DialogFooter, DialogHeading } from './Dialog'
import { Warning } from '~/icons'

interface AlertDialogProps extends Omit<DialogProps, 'children'> {
  title: string
  children: ReactNode
  variant?: 'confirmation' | 'information' | 'destructive' | 'error' | 'warning' // https://www.figma.com/design/wXVx2NssLo2wvnb9I0qkzM/01---Pattern-Library?node-id=1502-12660&m=dev
  actionLabel?: string | ReactNode
  actionIcon?: ReactNode
  cancelLabel?: string
  cancelIcon?: ReactNode
  onAction?: () => void
}

export function AlertDialog({
  title,
  variant,
  cancelLabel,
  cancelIcon,
  actionLabel,
  actionIcon,
  onAction,
  children,
  ...props
}: AlertDialogProps) {
  const getIconLabel = useCallback((variant?: string) => {
    switch (variant) {
      case 'error':
        return <Warning className="fill-primary-orange" />
      case 'warning':
        return <Warning className="fill-primary-orange" />
      default:
        return null
    }
  }, [])

  const getActionLabel = useCallback((variant?: string) => {
    switch (variant) {
      case 'confirmation':
        return 'Confirmar'
      case 'information':
      case 'warning':
        return 'Continuar'
      case 'destructive':
        return 'Excluir'
      default:
        return 'OK'
    }
  }, [])

  return (
    <Dialog role="alertdialog" {...props}>
      {({ close }) => (
        <>
          <DialogHeading slot="title">
            <span>{title}</span>
            {getIconLabel(variant)}
          </DialogHeading>
          <DialogBody>{children}</DialogBody>
          <DialogFooter>
            {variant && !['error'].includes(variant) ? (
              <Button className="flex items-center" color="secondary" isOutlined onPress={close}>
                {cancelIcon}
                {cancelLabel || 'Cancelar'}
              </Button>
            ) : null}

            <Button
              className="flex items-center"
              color={variant === 'confirmation' ? 'primary' : 'secondary'}
              autoFocus
              onPress={chain(onAction, close)}
            >
              {actionIcon}
              {actionLabel || getActionLabel(variant)}
            </Button>
          </DialogFooter>
        </>
      )}
    </Dialog>
  )
}
